import React, { useState, useMemo, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  InputNumber,
  Space,
  Divider,
  Spin,
  Button,
  Row,
  Col,
  Card,
  Typography,
  Switch,
  Tag,
  Alert,
  Steps,
  DatePicker,
  Skeleton,
  Modal,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import ImageWithSpinner from "../content/imagewithspinner";
import FormButton from "../common/formbutton";
import StoreSelectionManager from "../common/StoreSelectionManager";
import { renderBadgeSwatch, getBadgeColorName } from "./upsellbadgeutils";
import { TriggeredUpsellDetailDto } from "../../types/upselltypes";
import { lookupMenuItems, getMenuItemDetail } from "../../services/menuservice";
import { MenuItemLookup } from "../../types/menutypes";
import debounce from "lodash/debounce";

const { Text } = Typography;
const { Option } = Select;
const { Step } = Steps;

interface TriggerItemSearchProps {
  source?: string;
  value?: string[];
  onChange?: (val: string[]) => void;
  onItemsLoaded?: (items: MenuItemLookup[]) => void;
  initialItems?: MenuItemLookup[];
}

const TriggerItemSearch: React.FC<TriggerItemSearchProps> = ({
  source,
  value,
  onChange,
  onItemsLoaded,
  initialItems,
}) => {
  const [items, setItems] = useState<MenuItemLookup[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);

  const fetchItems = async (query: string, append = false) => {
    if (!source) return;
    setLoading(true);
    try {
      const data = await lookupMenuItems(source, query, 20);
      setItems((prev) => {
        const combined = append ? [...prev, ...data.items] : data.items;
        // Deduplicate to prevent rendering errors
        const unique = Array.from(
          new Map(
            combined.map((item) => [
              source === "product" && item.posCode
                ? item.posCode
                : item.menuGuid,
              item,
            ]),
          ).values(),
        );
        return unique as MenuItemLookup[];
      });
      if (onItemsLoaded) onItemsLoaded(data.items);
    } catch {
      // handle error if needed
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useMemo(
    () => debounce((q: string) => fetchItems(q), 300),
    [source],
  );

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      setInitialLoad(true);
      await fetchItems("");

      // Attempt to load metadata for pre-selected items so their names appear instead of raw IDs
      if (initialItems && initialItems.length > 0) {
        setItems((prev) => {
          const combined = [...prev, ...initialItems];
          const unique = Array.from(
            new Map(
              combined.map((item) => [
                source === "product" && item.posCode
                  ? item.posCode
                  : item.menuGuid,
                item,
              ]),
            ).values(),
          );
          return unique as MenuItemLookup[];
        });
        if (onItemsLoaded) onItemsLoaded(initialItems);
      } else if (value && value.length > 0) {
        await Promise.allSettled(value.map((val) => fetchItems(val, true)));
      }

      if (isMounted) setInitialLoad(false);
    };

    initialize();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  if (initialLoad) {
    return (
      <Select
        mode="multiple"
        disabled
        placeholder="Loading items..."
        loading
        style={{ width: "100%" }}
      />
    );
  }

  return (
    <Select
      mode="multiple"
      showSearch
      placeholder="Search items..."
      filterOption={false}
      onSearch={debouncedSearch}
      onChange={onChange}
      value={value}
      notFoundContent={loading ? <Spin size="small" /> : null}
      disabled={!source}
    >
      {items.map((i) => {
        const val = source === "product" && i.posCode ? i.posCode : i.menuGuid;
        return (
          <Option key={val} value={val}>
            {i.name} {i.posCode ? `(${i.posCode})` : ""}
          </Option>
        );
      })}
    </Select>
  );
};

const LoadingSelectMask: React.FC<any> = ({ loadingMask, ...props }) => {
  if (loadingMask) {
    return (
      <Select
        disabled
        placeholder="Loading item..."
        loading
        style={{ width: "100%" }}
      />
    );
  }
  return <Select {...props} />;
};

interface Props {
  open: boolean;
  editing: TriggeredUpsellDetailDto | null;
  form: any;
  onClose: () => void;
  onSave: () => Promise<void>;
  storeGuid: string;
  loadingForm: boolean;
  handleUnsavedChanges?: (hasChanges: boolean) => void;
}

const TriggeredUpsellBuilder: React.FC<Props> = ({
  open,
  editing,
  form,
  onClose,
  onSave,
  storeGuid,
  loadingForm,
  handleUnsavedChanges,
}) => {
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [items, setItems] = useState<MenuItemLookup[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [initialSuggestLoading, setInitialSuggestLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [itemNamesMap, setItemNamesMap] = useState<Record<string, string>>({});
  // Effect to handle unsaved changes warning (both React Router layout and native browser)
  useEffect(() => {
    handleUnsavedChanges?.(isDirty);

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    if (isDirty) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty, handleUnsavedChanges]);

  // Make sure we clear dirty state when drawer closes
  useEffect(() => {
    if (!open) {
      setIsDirty(false);
      handleUnsavedChanges?.(false);
    }
  }, [open, handleUnsavedChanges]);

  const handleCloseRequest = () => {
    if (isDirty) {
      Modal.confirm({
        title: "Unsaved Changes",
        content: "You have unsaved changes. Are you sure you want to leave?",
        okText: "Leave",
        cancelText: "Stay",
        onOk: () => {
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  const handleItemsLoaded = (
    loadedItems: MenuItemLookup[],
    itemSource: string,
  ) => {
    setItemNamesMap((prev) => {
      const next = { ...prev };
      loadedItems.forEach((i) => {
        const val =
          itemSource === "product" && i.posCode ? i.posCode : i.menuGuid;
        if (val) next[val] = i.name;
      });
      return next;
    });
  };

  useEffect(() => {
    if (editing?.prefetchedItems && editing.prefetchedItems.length > 0) {
      setItemNamesMap((prev) => {
        const next = { ...prev };
        editing.prefetchedItems!.forEach((i) => {
          if (i.posCode) next[i.posCode] = i.name;
          if (i.menuGuid) next[i.menuGuid] = i.name;
        });
        return next;
      });
    }
  }, [editing?.prefetchedItems]);

  const matchTrigger = Form.useWatch("matchTrigger", form);
  const allowSameAsCart = Form.useWatch("allowSameAsCart", form);
  const suggestSource = Form.useWatch("suggestSource", form);
  const imageUrl = Form.useWatch("imageUrl", form);
  const badgeText = Form.useWatch("badgeText", form);
  const badgeStyle = Form.useWatch("badgeStyle", form);
  const displayName = Form.useWatch("displayName", form);
  const description = Form.useWatch("description", form);
  const price = Form.useWatch("price", form);
  const validFrom = Form.useWatch("validFrom", form);
  const validUntil = Form.useWatch("validUntil", form);

  const formValues = Form.useWatch([], form) as any;
  const ifCartContains = formValues?.trigger?.ifCartContains;
  const hasTriggerConfig = ifCartContains && ifCartContains.length > 0;

  const fetchItems = async (query = "", append = false) => {
    if (!suggestSource && !matchTrigger) return;
    setLoadingItems(true);
    try {
      const data = await lookupMenuItems(suggestSource || "menu", query, 20);
      setItems((prev) => {
        const combined = append ? [...prev, ...data.items] : data.items;
        const unique = Array.from(
          new Map(
            combined.map((item) => [
              suggestSource === "product" && item.posCode
                ? item.posCode
                : item.menuGuid,
              item,
            ]),
          ).values(),
        );
        return unique as MenuItemLookup[];
      });
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      setInitialSuggestLoading(true);
      await fetchItems("");

      // Attempt to load metadata for pre-selected suggestion so its name appears instead of raw ID
      const initialId =
        suggestSource === "product"
          ? form.getFieldValue("suggestItemId")
          : form.getFieldValue("suggestMenuGuid");
      if (initialId) {
        if (editing?.prefetchedItems && editing.prefetchedItems.length > 0) {
          setItems((prev) => {
            const combined = [...prev, ...editing.prefetchedItems!];
            const unique = Array.from(
              new Map(
                combined.map((item) => [
                  suggestSource === "product" && item.posCode
                    ? item.posCode
                    : item.menuGuid,
                  item,
                ]),
              ).values(),
            );
            return unique as MenuItemLookup[];
          });
        } else {
          await fetchItems(initialId, true);
        }
      }

      if (isMounted) setInitialSuggestLoading(false);
    };

    initialize();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestSource]);

  useEffect(() => {
    if (matchTrigger) {
      form.setFieldsValue({
        suggestSource: undefined,
        suggestMenuGuid: undefined,
        suggestItemId: undefined,
        displayName: undefined,
        price: undefined,
        ageRestricted: undefined,
      });
      setItems([]);
    }
  }, [matchTrigger, form]);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => fetchItems(value), 300),
    [suggestSource],
  );

  const handleMenuSelection = async (selectedValue: string) => {
    if (!selectedValue || !storeGuid) return;

    const selectedItem = items.find((i) =>
      suggestSource === "product"
        ? i.posCode === selectedValue
        : i.menuGuid === selectedValue,
    );

    const realMenuGuid =
      selectedItem?.menuGuid ||
      (suggestSource === "menu" ? selectedValue : null);

    if (suggestSource === "product" && selectedItem) {
      form.setFieldValue("suggestMenuGuid", selectedItem.menuGuid);
    } else {
      form.setFieldValue("suggestItemId", null);
    }

    if (!realMenuGuid) return;

    setLoadingDetail(true);
    try {
      const detail = await getMenuItemDetail(realMenuGuid, storeGuid);
      if (detail) {
        form.setFieldsValue({
          displayName: detail.name,
          imageUrl: detail.imageUrl,
          price: detail.price,
          ageRestricted: detail.ageRestricted,
        });
      }
    } catch {
      // handle error
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
      setSaving(true);
      await onSave();
    } catch (e) {
      // validation failed
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    try {
      if (currentStep === 0) {
        const cartContains =
          form.getFieldValue(["trigger", "ifCartContains"]) || [];
        const conditionPaths = cartContains.flatMap((_: any, idx: number) => [
          ["trigger", "ifCartContains", idx, "source"],
          ["trigger", "ifCartContains", idx, "ids"],
          ["trigger", "ifCartContains", idx, "minQty"],
          ["trigger", "ifCartContains", idx, "maxQty"],
        ]);
        await form.validateFields([
          "description",
          "validFrom",
          "validUntil",
          ...conditionPaths,
        ]);
      } else if (currentStep === 1) {
        const fieldsToValidate = ["imageUrl"];
        if (!matchTrigger) {
          const source = form.getFieldValue("suggestSource");
          const itemField =
            source === "product" ? "suggestItemId" : "suggestMenuGuid";
          fieldsToValidate.push("suggestSource", itemField);
        }
        await form.validateFields(fieldsToValidate);
      } else if (currentStep === 2) {
        await form.validateFields(["badgeText", "badgeStyle", "reason"]);
      } else if (currentStep === 3) {
        await form.validateFields(["assignedStoreNumbers"]);
      }
      setCurrentStep(currentStep + 1);
    } catch (error) {
      // stay on step
    }
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };


  const renderLiveSummary = () => (
    <Card
      title="Rule Summary"
      size="small"
      style={{
        position: "sticky",
        top: 16,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        borderRadius: "8px",
        border: "none",
        overflow: "hidden",
      }}
      headStyle={{
        color: "white",
        backgroundColor: "var(--color-primary)",
        borderBottom: "none",
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong>Description:</Text>
        <div>{description || <Text type="secondary">Not set</Text>}</div>
      </div>
      <Divider style={{ margin: "12px 0" }} />
      <div style={{ marginBottom: 16 }}>
        <Text strong>Validity Period:</Text>
        <div>
          {validFrom || validUntil ? (
            <Text>
              {validFrom ? validFrom.format("MMM D, YYYY h:mm A") : "Anytime"} -{" "}
              {validUntil
                ? validUntil.format("MMM D, YYYY h:mm A")
                : "Indefinitely"}
            </Text>
          ) : (
            <Text type="secondary">Always valid</Text>
          )}
        </div>
      </div>
      <Divider style={{ margin: "12px 0" }} />
      <div style={{ marginBottom: 16 }}>
        <Text strong>IF Cart Contains:</Text>
        {(!ifCartContains || ifCartContains.length === 0) && (
          <div>
            <Text type="secondary">No conditions set</Text>
          </div>
        )}
        {ifCartContains &&
          ifCartContains.map((cond: any, idx: number) => {
            if (!cond) return null;
            return (
              <div key={idx} style={{ paddingLeft: 8, marginTop: 4 }}>
                {idx > 0 && (
                  <Tag color="blue" style={{ marginBottom: 4 }}>
                    AND
                  </Tag>
                )}
                <div>
                  <Text>{cond.minQty || 1}x of ANY </Text>
                  {!cond.ids || cond.ids.length === 0 ? (
                    <Text type="secondary">(No items selected)</Text>
                  ) : (
                    <ul style={{ paddingLeft: 16, margin: "4px 0 0 0" }}>
                      {cond.ids.map((id: string) => (
                        <li key={id}>
                          {itemNamesMap[id] ? (
                            <Text type="secondary">{itemNamesMap[id]}</Text>
                          ) : (
                            <Space size="small">
                              <Spin size="small" />
                              <Text type="secondary" italic>
                                Loading...
                              </Text>
                            </Space>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
      </div>
      <Divider style={{ margin: "12px 0" }} />
      <div style={{ marginBottom: 16 }}>
        <Text strong>THEN Suggest:</Text>
        {matchTrigger ? (
          <div>
            <Text italic>The exact item that triggered the rule</Text>
          </div>
        ) : (
          <div>
            {displayName ? (
              <Text>{displayName}</Text>
            ) : (
              <Text type="secondary">Item not selected</Text>
            )}
          </div>
        )}
      </div>
      <Divider style={{ margin: "12px 0" }} />
      <div style={{ marginBottom: 16 }}>
        <Text strong>Behavior Options:</Text>
        <div style={{ marginTop: 4 }}>
          <Space direction="vertical" size={0}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {matchTrigger
                ? "• Suggests triggering item"
                : "• Suggests explicitly configured item"}
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {allowSameAsCart
                ? "• Can suggest items already in cart"
                : "• Hides if item is already in cart"}
            </Text>
          </Space>
        </div>
      </div>
      <Divider style={{ margin: "12px 0" }} />
      <div>
        <Text strong>WITH BADGE:</Text>
        <div>
          {badgeText || badgeStyle ? (
            <Space>
              <Text>"{badgeText || "No text"}"</Text>
              {badgeStyle && (
                <Text type="secondary">
                  ( {renderBadgeSwatch(badgeStyle)}
                  {getBadgeColorName(badgeStyle)})
                </Text>
              )}
            </Space>
          ) : (
            <Text type="secondary">No badge configured</Text>
          )}
        </div>
      </div>
      <Divider style={{ margin: "12px 0" }} />
      <div>
        <Text strong>STORES:</Text>
        <div>
          {formValues?.assignedStoreNumbers?.length > 0 ? (
            <Text>{formValues.assignedStoreNumbers.length} assigned</Text>
          ) : (
            <Text type="secondary">No stores assigned</Text>
          )}
        </div>
      </div>
    </Card>
  );

  const renderStep0 = () => (
    <div style={{ display: currentStep === 0 ? "block" : "none" }}>
      <Form.Item
        name="description"
        label="Rule Description"
        rules={[{ required: true, message: "Required" }]}
      >
        <Input placeholder="e.g. Buy 1 Yogurt, Upsell Another 2-For Deal" />
      </Form.Item>
      <Divider orientation="left">Validity Period</Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="validFrom" label="Valid From">
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="validUntil" label="Valid Until">
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      </Row>
      <Divider orientation="left">Trigger Configuration</Divider>
      <Form.List name={["trigger", "ifCartContains"]}>
        {(fields, { add, remove }) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              marginBottom: 24,
            }}
          >
            {fields.map(({ key, name, ...restField }, index) => (
              <React.Fragment key={key}>
                {index > 0 && (
                  <Divider plain style={{ margin: "8px 0", borderBlockStartColor: "#1890ff" }}>
                    <Tag color="blue" style={{ margin: 0, padding: "0 16px", fontSize: 14 }}>AND</Tag>
                  </Divider>
                )}
                <Card
                  size="small"
                  type="inner"
                  title={
                    <Space>
                      <Text strong>Condition {index + 1}</Text>
                    </Space>
                  }
                extra={
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(name)}
                  />
                }
              >
                <Row gutter={16} align="middle">
                  <Col span={6}>
                    <Form.Item
                      {...restField}
                      name={[name, "source"]}
                      rules={[{ required: true }]}
                      label="Source"
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder="Select source"
                        onChange={() =>
                          form.setFieldValue(
                            ["trigger", "ifCartContains", name, "ids"],
                            [],
                          )
                        }
                      >
                        <Option value="menu">Menu</Option>
                        <Option value="product">Product</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(p, c) =>
                        p.trigger?.ifCartContains?.[name]?.source !==
                        c.trigger?.ifCartContains?.[name]?.source
                      }
                    >
                      {({ getFieldValue }) => (
                        <Form.Item
                          {...restField}
                          name={[name, "ids"]}
                          rules={[
                            { required: true, message: "Required" },
                            {
                              type: "array",
                              min: 1,
                              message: "Select at least one",
                            },
                          ]}
                          label="Match Items"
                          style={{ marginBottom: 0 }}
                        >
                          <TriggerItemSearch
                            value={getFieldValue(["trigger", "ifCartContains", name, "ids"])}
                            onChange={(val) => {
                              const triggers = form.getFieldValue("trigger") || {};
                              const contains = [...(triggers.ifCartContains || [])];
                              contains[name] = { ...contains[name], ids: val };
                              form.setFieldsValue({
                                trigger: { ...triggers, ifCartContains: contains },
                              });
                            }}
                            source={getFieldValue([
                              "trigger",
                              "ifCartContains",
                              name,
                              "source",
                            ])}
                            onItemsLoaded={(items) =>
                              handleItemsLoaded(
                                items,
                                getFieldValue([
                                  "trigger",
                                  "ifCartContains",
                                  name,
                                  "source",
                                ]),
                              )
                            }
                            initialItems={editing?.prefetchedItems}
                          />
                          <div style={{ marginTop: 6, lineHeight: 1.2 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              * Multiple items act as an <Tag color="warning" style={{ margin: "0 4px", fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>OR</Tag> condition
                            </Text>
                          </div>
                        </Form.Item>
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      {...restField}
                      name={[name, "minQty"]}
                      label="Min Qty"
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      {...restField}
                      name={[name, "maxQty"]}
                      label="Max Qty"
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
              </React.Fragment>
            ))}
            <Button
              type="dashed"
              onClick={() => add({ minQty: 1, maxQty: 1 })}
              block
              icon={<PlusOutlined />}
            >
              Add Trigger Condition
            </Button>
          </div>
        )}
      </Form.List>
      {!hasTriggerConfig && (
        <Alert
          message="Action Required"
          description="Please add at least one trigger condition above to proceed."
          type="warning"
          showIcon
        />
      )}
    </div>
  );

  const renderStep1 = () => (
    <div style={{ display: currentStep === 1 ? "block" : "none" }}>
      <Form.Item label="Suggest Behavior" style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Space align="center" size={8}>
            <Form.Item name="matchTrigger" valuePropName="checked" noStyle>
              <Switch size="small" />
            </Form.Item>
            <div style={{ width: 130 }}>
              <Text>Match Trigger</Text>
            </div>
            <Text type="secondary">
              - Suggests the exact item that triggered this rule instead of a
              configured item.
            </Text>
          </Space>
          <Space align="center" size={8}>
            <Form.Item name="allowSameAsCart" valuePropName="checked" noStyle>
              <Switch size="small" />
            </Form.Item>
            <div style={{ width: 130 }}>
              <Text>Allow Same as Cart</Text>
            </div>
            <Text type="secondary">
              - Allows the suggestion to appear even if the suggested item is
              already in the cart.
            </Text>
          </Space>
        </Space>
      </Form.Item>
      <Divider orientation="left">Suggestion Configuration</Divider>
      {matchTrigger ? (
        <Alert
          type="info"
          showIcon
          message="Matches Triggering Item"
          description="The suggested item is set to the most recently added matching cart item."
          style={{ marginBottom: 24 }}
        />
      ) : (
        <>
          <Form.Item
            name="suggestSource"
            label="Source"
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Select source"
              onChange={() => {
                form.setFieldsValue({
                  suggestMenuGuid: undefined,
                  suggestItemId: undefined,
                  displayName: undefined,
                  price: undefined,
                  ageRestricted: undefined,
                });
                setItems([]);
              }}
            >
              <Option value="menu">Menu</Option>
              <Option value="product">Product</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name={
              suggestSource === "product" ? "suggestItemId" : "suggestMenuGuid"
            }
            label="Search Item"
            rules={[{ required: true }]}
          >
            <LoadingSelectMask
              loadingMask={initialSuggestLoading}
              placeholder="Search items..."
              showSearch
              filterOption={false}
              onSearch={debouncedSearch}
              onChange={handleMenuSelection}
              notFoundContent={loadingItems ? <Spin size="small" /> : null}
            >
              {items.map((i) => {
                const val =
                  suggestSource === "product" && i.posCode
                    ? i.posCode
                    : i.menuGuid;
                if (!val) return null;
                return (
                  <Option key={val} value={val}>
                    {i.name} {i.posCode ? `(${i.posCode})` : ""}
                  </Option>
                );
              })}
            </LoadingSelectMask>
          </Form.Item>
        </>
      )}
      <Divider orientation="left">Image Override</Divider>
      <Spin spinning={loadingDetail}>
        <div style={{ display: "flex", gap: "1rem" }}>
          <div style={{ flex: 1 }}>
            <Form.Item name="imageUrl" label="Image URL" rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="Item image url" />
            </Form.Item>
          </div>
          <div
            style={{
              width: 100,
              height: 100,
              border: "1px dashed #ccc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {imageUrl ? (
              <ImageWithSpinner imageUrl={imageUrl} />
            ) : (
              <span style={{ color: "#ccc", fontSize: 12 }}>No Image</span>
            )}
          </div>
        </div>
        <div style={{ display: "none" }}>
          <Form.Item name="displayName">
            <Input />
          </Form.Item>
          <Form.Item name="price">
            <InputNumber />
          </Form.Item>
          <Form.Item name="ageRestricted">
            <InputNumber />
          </Form.Item>
        </div>
      </Spin>
    </div>
  );

  const renderStep2 = () => (
    <div style={{ display: currentStep === 2 ? "block" : "none" }}>
      <Divider orientation="left">Badging</Divider>
      <Form.Item name="badgeText" label="Badge Text">
        <Input placeholder="e.g. Best Value" maxLength={24} showCount />
      </Form.Item>
      <Form.Item name="badgeStyle" label="Badge Style">
        <Select placeholder="Select Style">
          <Option value="savings">{renderBadgeSwatch("savings")} {getBadgeColorName("savings")}</Option>
          <Option value="bundle">{renderBadgeSwatch("bundle")} {getBadgeColorName("bundle")}</Option>
          <Option value="loyalty">{renderBadgeSwatch("loyalty")} {getBadgeColorName("loyalty")}</Option>
          <Option value="withloyaltycardonly">{renderBadgeSwatch("withloyaltycardonly")} {getBadgeColorName("withloyaltycardonly")}</Option>
          <Option value="new">{renderBadgeSwatch("new")} {getBadgeColorName("new")}</Option>
          <Option value="popular">{renderBadgeSwatch("popular")} {getBadgeColorName("popular")}</Option>
          <Option value="limited">{renderBadgeSwatch("limited")} {getBadgeColorName("limited")}</Option>
        </Select>
      </Form.Item>
      <Form.Item name="reason" label="Upsell Reason">
        <Input />
      </Form.Item>
    </div>
  );

  const renderStep3 = () => (
    <div style={{ display: currentStep === 3 ? "block" : "none" }}>
      <Form.Item name="assignedStoreNumbers">
        <StoreSelectionManager
          assignedStores={form.getFieldValue("assignedStoreNumbers") || []}
          onChange={(stores) => form.setFieldValue("assignedStoreNumbers", stores)}
          title="Store Assignments"
          description="Select which stores should execute this triggered upsell."
        />
      </Form.Item>
    </div>
  );

  return (
    <Card
      size="small"
      style={{
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        borderRadius: "8px",
        overflow: "hidden",
        border: "none",
      }}
      title={
        <Space>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleCloseRequest}
            style={{ color: "white" }}
          />
          {editing ? "Edit Upsell" : "Add Upsell"}
        </Space>
      }
      headStyle={{
        color: "white",
        backgroundColor: "var(--color-primary)",
        borderBottom: "none",
      }}
    >
      <Skeleton active loading={loadingForm} paragraph={{ rows: 12 }}>
        <Row gutter={32}>
          <Col span={16}>
            <Steps current={currentStep} style={{ marginBottom: 32 }}>
              <Step title="Trigger" />
              <Step title="Suggestion" />
              <Step title="Badging" />
              <Step title="Stores" />
            </Steps>
            <Form
              form={form}
              layout="vertical"
              onValuesChange={() => setIsDirty(true)}
              initialValues={{
                matchTrigger: false,
                allowSameAsCart: false,
                trigger: { ifCartContains: [] },
                assignedStoreNumbers: [],
              }}
            >
              {renderStep0()}
              {renderStep1()}
              {renderStep2()}
              {renderStep3()}
            </Form>
            <div style={{ marginTop: 24, textAlign: "right" }}>
              <Space>
                {currentStep > 0 && (
                  <Button size="small" onClick={prev}>
                    Previous
                  </Button>
                )}
                {currentStep < 3 && (
                  <FormButton
                    title="Next"
                    icon={<ArrowRightOutlined />}
                    onClick={next}
                    disabled={currentStep === 0 && !hasTriggerConfig}
                  />
                )}
                {currentStep === 3 && (
                  <FormButton
                    title="Save Upsell"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={handleSave}
                  />
                )}
              </Space>
            </div>
          </Col>
          <Col span={8}>{renderLiveSummary()}</Col>
        </Row>
      </Skeleton>
    </Card>
  );
};

export default TriggeredUpsellBuilder;
